import type { ViewPrimitive, BladeContext } from '../registry';
import { BladeLoading, BladeError, BladeDenied, BladeEmpty, forcedBladeState } from '../BladeStates';

// Dashboard view-primitive (HUB-276) — KPI tiles + a labelled bar breakdown.
// Content layer: theme-adaptive brand utilities, no shell.css coupling, no chart
// dependency (bars are div widths). KPIs/bars come from `descriptor.params` (JSON)
// when present, else a sample. Real projection binding is a later slice (the data
// seam gains a dashboard fetcher); loading/empty/error/denied delegate to the catalog.

interface Kpi {
  label: string;
  value: string;
  delta?: string;
  trend?: 'up' | 'down' | 'flat';
}
interface Bar {
  label: string;
  value: number; // 0..100 (share of the row)
  amount?: string;
}

const SAMPLE_KPIS: Kpi[] = [
  { label: 'Open A/R', value: '$1.24M', delta: '−3.2%', trend: 'down' },
  { label: 'Clean claim rate', value: '94.1%', delta: '+1.4%', trend: 'up' },
  { label: 'Avg days in A/R', value: '29', delta: '−2', trend: 'down' },
  { label: 'Denials (30d)', value: '312', delta: '+18', trend: 'up' },
];

const SAMPLE_BARS: Bar[] = [
  { label: '0–30 days', value: 46, amount: '$571k' },
  { label: '31–60 days', value: 27, amount: '$335k' },
  { label: '61–90 days', value: 15, amount: '$186k' },
  { label: '90+ days', value: 12, amount: '$149k' },
];

function parse<T>(raw: string | undefined, fallback: T): T {
  if (!raw) return fallback;
  try {
    const v = JSON.parse(raw);
    return Array.isArray(v) && v.length ? (v as T) : fallback;
  } catch {
    return fallback;
  }
}

function trendClass(t: Kpi['trend']): string {
  if (t === 'up') return 'text-coronis-teal-600';
  if (t === 'down') return 'text-coronis-coral-500';
  return 'text-muted-foreground';
}

function DashboardBody({ ctx }: { ctx: BladeContext }) {
  const p = ctx.descriptor.params;
  const kpis = parse<Kpi[]>(p?.kpis, SAMPLE_KPIS);
  const bars = parse<Bar[]>(p?.bars, SAMPLE_BARS);

  return (
    <div className="flex flex-col gap-6">
      <div className="grid grid-cols-2 gap-3">
        {kpis.map((k) => (
          <div key={k.label} className="rounded-coronis border border-border px-3.5 py-3">
            <div className="text-xs text-muted-foreground">{k.label}</div>
            <div className="mt-1 text-2xl font-semibold text-foreground">{k.value}</div>
            {k.delta && <div className={`mt-0.5 text-xs font-medium ${trendClass(k.trend)}`}>{k.delta}</div>}
          </div>
        ))}
      </div>

      <div>
        <div className="mb-2 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
          Aging buckets
        </div>
        <div className="flex flex-col gap-2.5">
          {bars.map((b) => (
            <div key={b.label} className="flex items-center gap-3">
              <div className="w-24 shrink-0 text-xs text-muted-foreground">{b.label}</div>
              <div className="h-2.5 flex-1 overflow-hidden rounded-full bg-muted">
                <div
                  className="h-full rounded-full bg-coronis-blue-600"
                  style={{ width: `${Math.max(0, Math.min(100, b.value))}%` }}
                />
              </div>
              {b.amount && <div className="w-16 shrink-0 text-right text-xs text-foreground">{b.amount}</div>}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

export const dashboardPrimitive: ViewPrimitive = {
  key: 'dashboard',
  render: (ctx) => {
    const forced = forcedBladeState(ctx.descriptor);
    if (forced === 'loading') return <BladeLoading />;
    if (forced === 'error') return <BladeError />;
    if (forced === 'denied') return <BladeDenied />;
    if (forced === 'empty') {
      return <BladeEmpty title="No metrics" description="No data for this dashboard yet." />;
    }
    return <DashboardBody ctx={ctx} />;
  },
};
